import {
  ApiOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  MailOutlined,
  PieChartOutlined,
  QuestionCircleOutlined,
  RollbackOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { ConditionsIcon, XRayIconSquare } from '@mm/icons';
import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';
import { CompanyLevelFeatureFlag } from '@mm/state/model/CompanyModel';
import { ReactComponent as AudienceIcon } from 'assets/audiences-icon-left-menu.svg';
import { ReactComponent as IdcardOutlined } from 'assets/id-card.svg';
import { ReactComponent as ApplicationsIcon } from 'assets/more-icon-left-menu.svg';
import { ReactComponent as MailboxOutlined } from 'assets/mailbox.svg';
import { ReactComponent as BriefcaseOutlined } from 'assets/briefcase.svg';
import classNames from 'classnames';
import React from 'react';
import { HELP_BETA } from '../../common/constants/externalRoutes';

export type SidebarMenuDefinition = {
  id: string;
  name: string;
  icon?: (isActive: boolean) => React.ReactNode;
  description?: string;
  url: string;
  childRoutes?: string[];
  mustBeSysAdmin: boolean;
  hideForRole?: string[];
  featureFlag?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag;
  hideFeatureFlag?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag;
  historyState?: any;
  externalLink?: boolean;
};

export type SidebarTitleDefinition = {
  id: string;
  text: string;
  description?: string;
  icon?: (isActive: boolean) => React.ReactNode;
  menuItems: SidebarMenuDefinition[];
  category?: 'tools' | 'contacts' | 'optimize';
  url?: string;
  hideOnMobile: boolean;
  featureFlag?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag;
  hideFeatureFlag?: CompanyLevelFeatureFlag | SubscriptionFeatureFlag;
  externalLink?: boolean;
};

export const SIDEBAR_MENU_GROUPS = ['tools', 'contacts', 'optimize'];

const iconStyleProps = (isActive: boolean) => ({
  width: 20,
  height: 20,
  fontSize: 20,
  color: isActive ? '#262626' : '#8CA0B5',
});

const activeIconStyles = { ...iconStyleProps(true) };

export const SIDEBAR_MENU: SidebarTitleDefinition[] = [
  {
    id: 'DASHBOARD',
    text: 'Dashboard',
    url: '/dashboard',
    hideOnMobile: false,
    icon: () => <HomeOutlined style={activeIconStyles} />,
    menuItems: [],
  },
  {
    id: 'XRAY',
    text: 'Activate X-Ray Pixel',
    description: 'Gather leads from your website traffic',
    url: '/x-ray',
    hideOnMobile: false,
    icon: () => <XRayIconSquare color="#262626" size={20} />,
    menuItems: [],
  },
  {
    id: 'INTEGRATIONS',
    text: 'Integrations',
    description:
      'Connect your CRM and other platforms directly to Customers.ai',
    url: '/integrations',
    icon: () => <ApiOutlined style={activeIconStyles} />,
    menuItems: [],
    hideOnMobile: false,
  },
  {
    id: 'KLAVIYO',
    text: 'Klaviyo Integration',
    description: 'Connect your Klaviyo account directly to Customers.ai',
    url: '/klaviyo-integrations',
    icon: () => <ApiOutlined style={activeIconStyles} />,
    menuItems: [],
    hideOnMobile: false,
    featureFlag: 'compliant_klaviyo_app',
  },
  {
    id: 'AUTOMATIONS',
    text: 'Automations',
    url: '/automations',
    description: 'Create custom outbound automations.',
    hideOnMobile: false,
    icon: () => <ConditionsIcon color="#262626" size={20} />,
    menuItems: [
      {
        id: 'EMAIL_OUTREACH',
        name: 'Email Outreach',
        url: '/automations',
        icon: (isActive: boolean) => (
          <MailboxOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
      {
        id: 'EMAIL_TOOLS',
        name: 'Email Tools',
        url: '/email-tools',
        featureFlag: 'nativeIntegrations',
        icon: (isActive: boolean) => (
          <MailOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
    ],
    featureFlag: 'automations' as CompanyLevelFeatureFlag,
  },
  {
    id: 'CONTACTS',
    text: 'My Contacts',
    description:
      'All of your contacts in one place. Sort, filter, and create audiences.',
    url: '/contacts',
    hideOnMobile: false,
    icon: () => <TeamOutlined style={activeIconStyles} />,
    menuItems: [
      {
        id: 'contacts',
        name: 'My Contacts',
        url: '/contacts',
        icon: (isActive: boolean) => (
          <IdcardOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
      {
        id: 'audiences',
        name: 'Audiences',
        url: '/audiences',
        icon: (isActive: boolean) => (
          <AudienceIcon
            style={iconStyleProps(isActive)}
            className={classNames('sidebar-menu-group__icon-audiences', {
              'sidebar-menu-group__icon-audiences-black': isActive,
            })}
          />
        ),
        mustBeSysAdmin: false,
      },
      {
        id: 'signal',
        name: 'Signal',
        url: '/signal',
        icon: (isActive: boolean) => (
          <CheckCircleOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
    ],
  },

  {
    id: 'ANALYTICS',
    text: 'Analytics',
    url: '/xray-analytics',
    icon: () => <BarChartOutlined style={activeIconStyles} />,
    menuItems: [
      {
        id: 'XRAY_ANALYTICS',
        name: 'X-Ray Pixel',
        url: '/xray-analytics',
        icon: (isActive: boolean) => (
          <BarChartOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
      {
        id: 'FB_RESTORE_ANALYTICS',
        name: 'Restore',
        url: '/restore-analytics',
        icon: (isActive: boolean) => (
          <RollbackOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
      {
        id: 'DEMOGRAPHIC_ANALYTICS',
        name: 'Demographic Data',
        url: '/demographic-analytics',
        featureFlag: 'analytics',
        icon: (isActive: boolean) => (
          <PieChartOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
      {
        id: 'EMAIL_ANALYTICS',
        name: 'Email',
        url: '/outreach-analytics',
        featureFlag: 'analytics',
        icon: (isActive: boolean) => (
          <BarChartOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
    ],
    hideOnMobile: false,
  },
  {
    id: 'DIRECTORY',
    text: 'Directory',
    url: '/b2b-leads',
    icon: (isActive: boolean) => (
      <UsergroupAddOutlined style={iconStyleProps(isActive)} />
    ),
    hideOnMobile: false,
    menuItems: [
      {
        id: 'mercury',
        name: 'Consumer Directory',
        url: '/mercury',
        icon: (isActive: boolean) => (
          <UsergroupAddOutlined style={iconStyleProps(isActive)} />
        ),
        mustBeSysAdmin: false,
      },
      {
        id: 'voyager',
        name: 'B2B Contacts',
        description: 'Purchase targeted B2B leads.',
        icon: (isActive: boolean) => (
          <BriefcaseOutlined style={iconStyleProps(isActive)} />
        ),
        url: '/b2b-leads',
        mustBeSysAdmin: false,
      },
    ],
  },
  {
    id: 'OTHER',
    text: 'Old Features',
    url: '/inbox',
    hideOnMobile: false,
    icon: () => <ApplicationsIcon style={activeIconStyles} />,
    menuItems: [
      {
        id: 'INBOX',
        name: 'Inbox',
        url: '/live-chat',
        mustBeSysAdmin: false,
        hideForRole: ['monitor_leads'],
      },
      {
        id: 'CHAT_STARTERS',
        name: 'Chat Starters',
        url: '/chat-starters',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'DIALOGUES',
        name: 'Dialogue Library',
        url: '/build',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'TRIGGER',
        name: 'Q&A',
        url: '/trigger',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'CAMPAIGNS',
        name: 'Campaigns',
        url: '/campaigns',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'RULES',
        name: 'Automation Rules',
        url: '/automation-rules',
        hideForRole: ['monitor_leads', 'live_chat_agent'],
        mustBeSysAdmin: false,
      },
      {
        id: 'BOT_ANALYTICS',
        name: 'Bot Analytics',
        url: '/bot-analytics',
        mustBeSysAdmin: false,
      },
      {
        id: 'ADS_ANALYTICS',
        name: 'Ads Analytics',
        url: '/ads-analytics',
        featureFlag: 'analyticsAds',
        mustBeSysAdmin: false,
      },
      {
        id: 'SETTINGS',
        name: 'Settings',
        url: '/settings',
        mustBeSysAdmin: false,
      },
    ],
  },

  {
    id: 'HELP',
    text: 'Help',
    url: HELP_BETA,
    externalLink: true,
    hideOnMobile: false,
    icon: () => (
      <QuestionCircleOutlined
        style={{ ...activeIconStyles, color: '#8CA0B5' }}
      />
    ),
    menuItems: [],
  },
];
